import { StateCreator } from 'zustand';

import { logger } from '../../lib/logger';
import { Choice, NarrativeState, Node, SegmentId } from '../../types/narrative';
import { initializeNarrativeState, updateNodeContent } from '../../utils/narrativeHelpers';

const narrativeLogger = logger.child('[NarrativeSlice]');

export interface NarrativeSlice {
  // State
  narrativeState: NarrativeState;

  // Actions
  updateNode: (nodeId: string, updates: Partial<Node>) => void;
  setCurrentNode: (nodeId: string) => void;
  setCurrentSegment: (segmentId: SegmentId) => void;
  updateNodeText: (nodeId: string, text: string) => void;
  updateNodeChoice: (nodeId: string, choiceIndex: number, updates: Partial<Choice>) => void;
  setVariable: (name: string, value: any) => void;
  initializeNarrative: () => void;
  resetNarrativeState: () => void;
}

export const createNarrativeSlice: StateCreator<any, [], [], NarrativeSlice> = (
  set: any,
  get: any
) => ({
  // Initial state
  narrativeState: initializeNarrativeState(),

  // Update entire node
  updateNode: (nodeId, updates) =>
    set((state: any) => {
      narrativeLogger.debug('updateNode called', {
        nodeId,
        updates,
        currentState: state.narrativeState.nodes[nodeId],
      });
      const newState = updateNodeContent(state.narrativeState, nodeId, updates);
      narrativeLogger.debug('updateNode result', { nodeId, newNodeState: newState.nodes[nodeId] });
      return { narrativeState: newState, hasUnsavedChanges: true };
    }),

  // Set current node
  setCurrentNode: (nodeId) => {
    set((state: any) => {
      const node = state.narrativeState.nodes[nodeId];
      if (!node) return state;

      return {
        narrativeState: {
          ...state.narrativeState,
          currentNodeId: nodeId,
          currentSegmentId: node.segmentId,
        },
        hasUnsavedChanges: true,
      };
    });

    // Save current project to persist node position
    setTimeout(() => {
      const state = get();
      if (state.currentProjectId && state.saveCurrentProject) {
        state.saveCurrentProject();
      }
    }, 0);
  },

  // Set current segment
  setCurrentSegment: (segmentId) => {
    set((state: any) => ({
      narrativeState: {
        ...state.narrativeState,
        currentSegmentId: segmentId,
      },
      hasUnsavedChanges: true,
    }));

    // Save current project to persist segment position
    setTimeout(() => {
      const state = get();
      if (state.currentProjectId && state.saveCurrentProject) {
        state.saveCurrentProject();
      }
    }, 0);
  },

  // Update node text
  updateNodeText: (nodeId, text) =>
    set((state: any) => {
      const newState = updateNodeContent(state.narrativeState, nodeId, { text });
      return { narrativeState: newState, hasUnsavedChanges: true };
    }),

  // Update a specific choice
  updateNodeChoice: (nodeId, choiceIndex, updates) =>
    set((state: any) => {
      const node = state.narrativeState.nodes[nodeId];
      if (!node || !node.choices) return state;

      // Validate choice index is within bounds
      if (choiceIndex < 0 || choiceIndex >= node.choices.length) {
        narrativeLogger.warn('Invalid choice index', {
          nodeId,
          choiceIndex,
          choicesLength: node.choices.length,
        });
        return state;
      }

      const newChoices = [...node.choices];
      newChoices[choiceIndex] = { ...newChoices[choiceIndex], ...updates };

      const newState = updateNodeContent(state.narrativeState, nodeId, { choices: newChoices });
      return { narrativeState: newState, hasUnsavedChanges: true };
    }),

  // Set a variable value
  setVariable: (name, value) =>
    set((state: any) => ({
      narrativeState: {
        ...state.narrativeState,
        variables: {
          ...state.narrativeState.variables,
          [name]: value,
        },
      },
      hasUnsavedChanges: true,
    })),

  // Initialize narrative state
  initializeNarrative: () =>
    set(() => ({
      narrativeState: initializeNarrativeState(),
      hasUnsavedChanges: true,
    })),

  // Reset narrative state
  resetNarrativeState: () =>
    set(() => ({
      narrativeState: initializeNarrativeState(),
      hasUnsavedChanges: true,
    })),
});
